import React from 'react';
import axios from 'axios';
import {
  Container,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Grid,
  Divider,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedTableCell as TableCell, LocalizedTextField as TextField } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { AutoAwesome } from '@mui/icons-material';
import CitySearch from '../components/forms/CitySearch';
import { Helmet } from 'react-helmet';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { formatDisplayDateTime } from '../utils/dateDisplay';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

interface City {
  id: string;
  name: string;
  displayName: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  timezone: {
    name: string;
    offset: number;
  };
  population: number;
  admin1?: {
    name: string;
    code?: string;
  };
  country?: {
    name: string;
    code: string;
  };
}

const NEW_DELHI_CITY: City = {
  id: 'new-delhi-default',
  name: 'New Delhi',
  displayName: 'New Delhi, Delhi, India',
  coordinates: { latitude: 28.6139, longitude: 77.2090 },
  timezone: { name: 'Asia/Kolkata', offset: 330 },
  population: 0,
  country: { name: 'India', code: 'IN' },
};

interface RulingPlanetsResult {
  lagnaLord: string;
  lagnaStarLord: string;
  moonSignLord: string;
  moonStarLord: string;
  dayLord: string;
  details: {
    ascDegree: number;
    moonDegree: number;
    ascendant: {
      sign: string;
      nakshatra: string;
      pada: number;
      degree: {
        formatted: string;
      };
    };
    moon: {
      sign: string;
      nakshatra: string;
      pada: number;
      degree: {
        formatted: string;
      };
    };
    calculatedAt: string;
  };
}

const formatDateForInput = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatTimeForInput = (date: Date): string => {
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

const RulingPlanets: React.FC = () => {
  const now = React.useMemo(() => new Date(), []);
  const { defaultCity } = useUserPreferences();
  const fallbackCity = React.useMemo(() => (defaultCity as City | null) || NEW_DELHI_CITY, [defaultCity]);
  const [selectedCity, setSelectedCity] = React.useState<City | null>(fallbackCity);
  const [selectedDate, setSelectedDate] = React.useState<string>(formatDateForInput(now));
  const [selectedTime, setSelectedTime] = React.useState<string>(formatTimeForInput(now));
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<RulingPlanetsResult | null>(null);

  React.useEffect(() => {
    document.title = 'Ruling Planets - Kundlistar - Nadi, KP, Vedic, Jaimini, Numerology Software';
  }, []);

  React.useEffect(() => {
    setSelectedCity((current) => (!current || current.id === NEW_DELHI_CITY.id ? fallbackCity : current));
  }, [fallbackCity]);

  const handleCalculate = async () => {
    if (!selectedCity) {
      setError('Please select a city first.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResult(null);

      const response = await axios.post(`${API_BASE_URL}/charts/ruling-planets`, {
        city: selectedCity.name,
        latitude: selectedCity.coordinates.latitude,
        longitude: selectedCity.coordinates.longitude,
        timezoneOffset: selectedCity.timezone.offset,
        date: selectedDate,
        time: selectedTime
      });

      setResult(response.data.data.rulingPlanets);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to calculate ruling planets');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Helmet>
        <title>Ruling Planets  | Accurate Timing | Kundlistar</title>
        <meta name="description" content="Calculate real-time Ruling Planets (RP) for KP and Nadi Astrology. Perfect for timing of events, birth time rectification, and verifying Prashna queries with 100% accuracy." />
        <meta name="keywords" content="Ruling Planets, KP Astrology RP, Nadi Astrology Timing, RP Table, Prashna Astrology Tool, Kundlistar" />

        {/* Open Graph tags for social sharing */}
        <meta property="og:title" content="Accurate Ruling Planets Tool for Professional Astrologers" />
        <meta property="og:description" content="Timing is everything. Use Kundlistar's real-time RP engine to decode the cosmic clock." />
        <meta property="og:type" content="website" />
      </Helmet>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ mb: 1, textAlign: 'center', fontWeight: 600 }}>
          Ruling Planets
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
          Select city, date, and time to calculate KP ruling planets
        </Typography>

        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="stretch">
              <Grid size={{ xs: 12, md: 6 }}>
                <CitySearch
                  value={selectedCity}
                  onChange={(city) => {
                    setSelectedCity(city);
                    setError(null);
                  } }
                  label="City"
                  placeholder="Search city..."
                  required />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  InputLabelProps={{ shrink: true }} />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  fullWidth
                  type="time"
                  label="Time"
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  InputLabelProps={{ shrink: true }} />
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AutoAwesome />}
                  onClick={handleCalculate}
                  disabled={loading || !selectedCity}
                  sx={{ height: '100%', minHeight: 56, fontWeight: 600 }}
                >
                  {loading ? 'Calculating...' : 'Calculate'}
                </Button>
              </Grid>
            </Grid>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </CardContent>
        </Card>

        {result && selectedCity && (
          <Box sx={{ mt: 4 }}>
            <Card sx={{ mb: 3, borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 0.5 }}>{selectedCity.displayName}</Typography>
                <Typography variant="body2" color="text.secondary">Calculated at: {formatDisplayDateTime(result.details.calculatedAt)}</Typography>
                <Typography variant="body2" color="text.secondary">Coordinates: {selectedCity.coordinates.latitude.toFixed(4)}, {selectedCity.coordinates.longitude.toFixed(4)}</Typography>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3, mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Ruling Planets</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Parameter</TableCell>
                        <TableCell>Planet</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>

                      <TableRow>
                        <TableCell>Lagna Lord</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(76, 175, 80, 0.14)' }}>{result.lagnaLord}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Lagna Star Lord</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(76, 175, 80, 0.14)' }}>{result.lagnaStarLord}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Moon Sign Lord</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(76, 175, 80, 0.14)' }}>{result.moonSignLord}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Moon Star Lord</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(76, 175, 80, 0.14)' }}>{result.moonStarLord}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Day Lord</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(76, 175, 80, 0.14)' }}>{result.dayLord}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Position Details</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Point</TableCell>
                        <TableCell>Sign</TableCell>
                        <TableCell>Degree</TableCell>
                        <TableCell>Nakshatra</TableCell>
                        <TableCell>Pada</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Ascendant</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(76, 175, 80, 0.14)' }}>{result.details.ascendant.sign}</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(76, 175, 80, 0.14)' }}>{result.details.ascendant.degree.formatted}</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(76, 175, 80, 0.14)' }}>{result.details.ascendant.nakshatra}</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(76, 175, 80, 0.14)' }}>{result.details.ascendant.pada}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Moon</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(76, 175, 80, 0.14)' }}>{result.details.moon.sign}</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(76, 175, 80, 0.14)' }}>{result.details.moon.degree.formatted}</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(76, 175, 80, 0.14)' }}>{result.details.moon.nakshatra}</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(76, 175, 80, 0.14)' }}>{result.details.moon.pada}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Divider sx={{ mt: 2, mb: 1 }} />
                <Typography variant="caption" color="primary.dark" sx={{ fontWeight: 600, bgcolor: 'rgba(76, 175, 80, 0.14)', px: 1, py: 0.5, borderRadius: 1, display: 'inline-block' }}>
                  Asc Longitude: {result.details.ascDegree.toFixed(6)} | Moon Longitude: {result.details.moonDegree.toFixed(6)}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}
      </Container></>
  );
};

export default RulingPlanets;
